import { useEffect, useState, useCallback } from 'react'
import { Contract } from 'ethers'
import { Erc20Abi } from '@shared/Abis'
import { EthAddr } from '@shared/SchemaTypes'
import { logOT } from '@shared/loggingShared'
import { useOtChainState } from '@react/redux/store'

interface UseErc20BalanceOptions {
  tokenAddress: EthAddr
  accountAddress: EthAddr
  enabled?: boolean
  refetchInterval?: number
}

interface UseErc20BalanceReturn {
  balance: bigint
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

export const useErc20Balance = ({
  tokenAddress,
  accountAddress,
  enabled = true,
  refetchInterval,
}: UseErc20BalanceOptions): UseErc20BalanceReturn => {
  const { chainConfiguration } = useOtChainState()
  const { websocketProvider } = chainConfiguration

  const [balance, setBalance] = useState<bigint>(0n)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchBalance = useCallback(async (): Promise<void> => {
    if (!websocketProvider || !tokenAddress || !accountAddress || !enabled) {
      return
    }

    try {
      setError(null)
      if (websocketProvider.websocket.readyState !== 1) {
        return
      }
      const contract = new Contract(tokenAddress, Erc20Abi, websocketProvider)
      const fetchedBalance = await contract.balanceOf(accountAddress)
      setBalance(BigInt(fetchedBalance))

      logOT.info('ERC20 balance fetched:', {
        tokenAddress,
        accountAddress,
        balance: fetchedBalance.toString(),
      })
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch balance')
      setError(error)
      logOT.error('Error fetching ERC20 balance:', error)
    } finally {
      setIsLoading(false)
    }
  }, [websocketProvider, tokenAddress, accountAddress, enabled])

  const refetch = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    await fetchBalance()
  }, [fetchBalance])

  useEffect(() => {
    if (!websocketProvider || !tokenAddress || !accountAddress || !enabled) {
      setIsLoading(false)
      return
    }

    let contract: Contract | null = null
    let isMounted = true
    let intervalId: NodeJS.Timeout | null = null

    const setupEventListeners = async () => {
      try {
        // Initial balance fetch
        await fetchBalance()

        if (!isMounted) return

        // Set up event listeners
        contract = new Contract(tokenAddress, Erc20Abi, websocketProvider)

        // Create specific filters for Transfer events involving accountAddress
        const transferFromFilter = contract.filters.Transfer(accountAddress, null, null)
        const transferToFilter = contract.filters.Transfer(null, accountAddress, null)

        const handleTransfer = (log: any) => {
          logOT.debug('handleTransfer', log)
          try {
            if (log?.args?.length >= 3) {
              logOT.info('Transfer event detected, updating balance:', {
                from: log.args[0],
                to: log.args[1],
                value: log.args[2].toString(),
                tokenAddress,
                accountAddress,
              })

              // Refetch balance after blockchain state update
              setTimeout(() => {
                if (isMounted) {
                  fetchBalance()
                }
              }, 1000)
            }
          } catch (parseError) {
            logOT.error('Error handling transfer event:', parseError)
          }
        }

        contract.on(transferFromFilter, handleTransfer)
        contract.on(transferToFilter, handleTransfer)

        // Optional polling fallback
        if (refetchInterval && refetchInterval > 0) {
          intervalId = setInterval(() => {
            if (isMounted) {
              fetchBalance()
            }
          }, refetchInterval)
        }
      } catch (err) {
        if (isMounted) {
          const error = err instanceof Error ? err : new Error('Failed to setup event listeners')
          setError(error)
          logOT.error('Error setting up ERC20 event listeners:', error)
        }
      }
    }

    setupEventListeners()

    // Cleanup function
    return () => {
      isMounted = false

      if (contract) {
        try {
          contract.removeAllListeners()
        } catch (err) {
          logOT.error('Error cleaning up ERC20 event listeners:', err)
        }
      }

      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [websocketProvider, tokenAddress, accountAddress, enabled, fetchBalance, refetchInterval])

  return {
    balance,
    isLoading,
    error,
    refetch,
  }
}
